import noop from 'lodash/noop';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectUserProjectTimeZone } from 'core/userContext';
import {
  DatePickerWithCollapsibleCalendar,
  TimePicker,
  TimePickerProps,
  TimeZonePicker,
} from 'ui/datePicker';
import { Popover, PopoverContent } from 'ui/popover';
import { WebLink2 } from 'ui/theme';
import { HelpPopoverIcon, HelpPopoverTrigger } from '../DisplayUntil.styles';
import DisplayUntilItem from '../DisplayUntilItem';
import { parseTimeStringToHoursAndMinutes } from './EndDateItem.utils';

interface EndDateItemProps {
  endDate: string;
  onChange?: (endDate?: string) => void;
}

const EndDateItem = ({ endDate, onChange = noop }: EndDateItemProps) => {
  const projectTimeZone = useSelector(selectUserProjectTimeZone);
  const validDateRange = useMemo(
    () => ({
      start: moment.tz(new Date(), projectTimeZone).add(24, 'hours'),
    }),
    [projectTimeZone],
  );
  const momentEndDate = useMemo(
    () => moment.tz(endDate, projectTimeZone),
    [endDate, projectTimeZone],
  );
  const time = useMemo(() => {
    return moment.tz(endDate, projectTimeZone).format('hh:mm A') as TimePickerProps['time'];
  }, [endDate, projectTimeZone]);

  const onDateChange = (date: moment.Moment) => {
    // Set the correct hour and minute
    date.set({ hour: momentEndDate.hour(), minute: momentEndDate.minute() });
    onChange(date.toJSON());
  };

  const onTimeChange = ([t]: string[]) => {
    const { hour, minute } = parseTimeStringToHoursAndMinutes(t);
    onChange(moment.tz(endDate, projectTimeZone).set({ hour, minute })?.toJSON());
  };

  return (
    <DisplayUntilItem onClose={() => onChange(undefined)}>
      Date is
      <DatePickerWithCollapsibleCalendar
        date={momentEndDate as moment.Moment}
        id="end-date-input"
        validDateRange={validDateRange}
        // @ts-ignore -- conflict of moment vs moment-timezone
        onDateChange={onDateChange}
      />
      <TimePicker time={time} hasClockIcon={true} onSelect={onTimeChange} />
      <TimeZonePicker timeZone={projectTimeZone} isDisabled={true} />
      <Popover>
        <PopoverContent
          side="top"
          LinkContent={
            <WebLink2
              href="https://support.iterable.com/hc/articles/23060529977364#placements-and-prioritization"
              target="_blank"
            >
              Learn more
            </WebLink2>
          }
        >
          <div>
            The total length of time the message will be visible to a user after it is first
            displayed during an embedded message session.
          </div>
        </PopoverContent>
        <HelpPopoverTrigger>
          <HelpPopoverIcon isColorInherited={true} />
        </HelpPopoverTrigger>
      </Popover>
    </DisplayUntilItem>
  );
};

export default EndDateItem;
